// @ts-check
import { useMutation } from "react-query";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useCandidateAPI } from "../shared/useAPI";
import { useAuth } from "./AuthContext";
import UAParser from "ua-parser-js";
import qs from "qs";

const useUserAgent = () => {
  const [parser] = useState(() => new UAParser());
  return useMemo(() => parser.getResult(), [parser]);
};

/** @type {() => (string)} */
export const useDeviceType = () => {
  const ua = useUserAgent();
  return ua.device.type != undefined ? ua.device.type : "LAPTOP";
};

/** @typedef {"MOBILE" | "LAPTOP"} UserDevice */
/** @type {() => UserDevice} */
export const useUserDevice = () => {
  const deviceType = useDeviceType();
  switch (deviceType) {
    case "tablet":
    case "mobile":
      return "MOBILE";
    default:
      return "LAPTOP";
  }
};

/** @type {() => import("react-query").UseMutationResult<{ registrationStage: 'JUST_REGISTERED' | 'EMAIL_CONFIRMED' }, unknown, { email: string, password: string }>} */
export const useRegisterMutation = () => {
  const api = useCandidateAPI();
  return useMutation(async (json) => {
    const userRegistration = {
      ...json,
      domainUniqueName: process.env.REACT_APP_APPLICATION_DOMAIN,
    }
    return api.post("public/recruiter/register", { json: userRegistration }).json();
  });
};

/** @type {() => import("react-query").UseMutationResult<{ registrationStage: 'JUST_REGISTERED' | 'EMAIL_CONFIRMED' }, unknown, { email: string, password: string }>} */
export const useLeadRegisterMutation = () => {
  const api = useCandidateAPI();
  return useMutation(async (json) => {
    const userRegistration = {
      ...json,
      domainUniqueName: process.env.REACT_APP_APPLICATION_DOMAIN,
    }
    return api.post("public/recruiter/leadregister", { json: userRegistration }).json();
  });
};

/** @typedef {{ token: string }} AuthenticationResult */

/** @type {() => import("react-query").UseMutationResult<AuthenticationResult, unknown, { email: string, password: string }>} */
export const useLoginMutation = () => {
  const history = useHistory();
  const { setAccessToken } = useAuth();
  const api = useCandidateAPI();
  return useMutation(async (params) => {
    const userLogin = {
      ...params,
      domainUniqueName: process.env.REACT_APP_APPLICATION_DOMAIN,
    }
    const result = await api
      .post("public/recruiter/login", {
        json: userLogin,
      })
      .json();
    setAccessToken(result.token);
    history.replace("/");
    return result;
  });
};

/** @type {() => import("react-query").UseMutationResult<AuthenticationResult, unknown, { email: string, password: string }>} */
export const useLoginMutationEnroll = () => {
  const history = useHistory();
  const { setAccessToken } = useAuth();
  const api = useCandidateAPI();
  return useMutation(async (params) => {
    const userLogin = {
      ...params,
      domainUniqueName: process.env.REACT_APP_APPLICATION_DOMAIN,
    }
    const result = await api
      .post("public/recruiter/login", {
        json: userLogin,
      })
      .json();
    setAccessToken(result.token);
    history.replace("/partnership");
    return result;
  });
};

/** @type {() => import("react-query").UseMutationResult<AuthenticationResult, unknown, { token: string, school: string, schoolname: string }>} */
export const useConfirmMutation = () => {
  const history = useHistory();
  const { setAccessToken } = useAuth();
  const api = useCandidateAPI();
  return useMutation(async (params) => {
    const result = await api
      .post("public/recruiter/confirm", {
        json: params,
      })
      .json();
    setAccessToken(result.token);
    history.replace({
      pathname: "confirmedAccount",
      search: qs.stringify(
        {
          school: params.school,
          schoolname: params.schoolname,
        },
        { addQueryPrefix: true }
      ),
    });
    return result;
  });
};

/** @type {() => import("react-query").UseMutationResult<void, unknown, { email: string }>} */
export const useForgotPasswordMutation = () => {
  const api = useCandidateAPI();
  return useMutation(async ({ email }) => {
    return api.post(`public/recruiter/domain/${process.env.REACT_APP_APPLICATION_DOMAIN}/forgot`, { body: email }).json();
  });
};

/** @type {() => import("react-query").UseMutationResult<void, unknown, { email: string }>} */
export const useCreatePasswordMutation = () => {
  const api = useCandidateAPI();
  return useMutation(async ({ email }) => {
    return api.post(`public/recruiter/domain/${process.env.REACT_APP_APPLICATION_DOMAIN}/mailforpasswordcreation`, { body: email }).json();
  });
};

/** @type {() => import("react-query").UseMutationResult<void, unknown, { token: string, newPassword: string }>} */
export const useResetPasswordMutation = () => {
  const api = useCandidateAPI();
  return useMutation(async (json) => {
    return api.post("public/recruiter/resetpassword", { json }).json();
  });
};
